import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { GET } from "APIclient";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  CircularProgress,
  Grid,
  Chip,
  Divider
} from "@mui/material";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import properties from "constants/ApplicationConstants";
import dayjs from "dayjs";

const ViewAsset = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { assetId } = location.state || {};
  const [asset, setAsset] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!assetId) {
      console.error("No asset ID provided");
      setLoading(false);
      return;
    }

    const fetchAsset = async () => {
      try {
        const response = await GET(`it-dept/asset/${assetId}`);
        if (response.data?.statusCode === 200) setAsset(response.data.data);
      } catch (err) {
        console.error("Error fetching asset details:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchAsset();
  }, [assetId]);

  if (loading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "400px" }}>
          <CircularProgress />
        </MDBox>
      </DashboardLayout>
    );
  }

  if (!asset) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox sx={{ textAlign: "center", mt: 5 }}>
          <MDTypography variant="h6" sx={{ fontFamily: properties.fontFamily, color: "text.secondary" }}>
            Asset not found
          </MDTypography>
        </MDBox>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox>
        <Card sx={{ borderRadius: 2, boxShadow: 3, minHeight:"90vh" }}>
          <CardContent sx={{ p: 3 }}>
            <Grid container justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
              <Grid item>
                <MDTypography
                  variant="h6"
                  sx={{
                    color: "textPrimary",
                    fontFamily: properties.fontFamily,
                    fontSize: "16px",
                    fontWeight: "bold",
                  }}
                >
                  Asset Details
                </MDTypography>
              </Grid>
              <Grid item>
                <MDButton
                  variant="outlined"
                  color="secondary"
                  onClick={() => navigate(-1)}
                  sx={{ fontFamily: properties.fontFamily }}
                >
                  Back to Asset List
                </MDButton>
              </Grid>
            </Grid>

            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <MDBox>
                  <MDTypography variant="h6" sx={{ fontFamily: properties.fontFamily, fontSize: "14px", fontWeight: "bold", color: "textPrimary" }}>
                    Asset Main Description
                  </MDTypography>
                  <MDTypography variant="h6" sx={{ fontFamily: properties.fontFamily, fontSize: "14px", color: "text.primary", py: 0.5 }}>
                    {asset.assetMainDescrip || asset.assetName || "N/A"}
                  </MDTypography>
                </MDBox>
              </Grid>

              <Grid item xs={12} md={4}>
                <MDBox>
                  <MDTypography variant="h6" sx={{ fontFamily: properties.fontFamily, fontSize: "14px", fontWeight: "bold", color: "textPrimary" }}>
                    Asset Number
                  </MDTypography>
                  <MDTypography variant="h6" sx={{ fontFamily: properties.fontFamily, fontSize: "14px", color: "text.primary", py: 0.5 }}>
                    {asset.assetNo || "N/A"}
                  </MDTypography>
                </MDBox>
              </Grid>

              <Grid item xs={12} md={4}>
                <MDBox>
                  <MDTypography variant="h6" sx={{ fontFamily: properties.fontFamily, fontSize: "14px", fontWeight: "bold", color: "textPrimary" }}>
                    Component Name
                  </MDTypography>
                  <MDTypography variant="h6" sx={{ fontFamily: properties.fontFamily, fontSize: "14px", color: "text.primary", py: 0.5 }}>
                    {asset.itComponent?.componentName || asset.component?.componentName || asset.componentName || "N/A"}
                  </MDTypography>
                </MDBox>
              </Grid>

              <Grid item xs={12} md={4}>
                <MDBox>
                  <MDTypography variant="h6" sx={{ fontFamily: properties.fontFamily, fontSize: "14px", fontWeight: "bold", color: "textPrimary" }}>
                    Purchase Date
                  </MDTypography>
                  <MDTypography variant="h6" sx={{ fontFamily: properties.fontFamily, fontSize: "14px", color: "text.primary", py: 0.5 }}>
                    {asset.purchaseDate ? dayjs(asset.purchaseDate).format("DD/MM/YYYY") : "N/A"}
                  </MDTypography>
                </MDBox>
              </Grid>

              <Grid item xs={12} md={4}>
                <MDBox>
                  <MDTypography variant="h6" sx={{ fontFamily: properties.fontFamily, fontSize: "14px", fontWeight: "bold", color: "textPrimary" }}>
                    Project
                  </MDTypography>
                  <MDTypography variant="h6" sx={{ fontFamily: properties.fontFamily, fontSize: "14px", color: "text.primary", py: 0.5 }}>
                    {asset.project || "N/A"}
                  </MDTypography>
                </MDBox>
              </Grid>

              <Grid item xs={12} md={4}>
                <MDBox>
                  <MDTypography variant="h6" sx={{ fontFamily: properties.fontFamily, fontSize: "14px", fontWeight: "bold", color: "textPrimary" }}>
                    Asset By
                  </MDTypography>
                  <MDTypography variant="h6" sx={{ fontFamily: properties.fontFamily, fontSize: "14px", color: "text.primary", py: 0.5 }}>
                    {asset.assetBy || "N/A"}
                  </MDTypography>
                </MDBox>
              </Grid>

              <Grid item xs={12}>
                <MDBox>
                  <MDTypography variant="h6" sx={{ fontFamily: properties.fontFamily, fontSize: "14px", fontWeight: "bold", color: "textPrimary" }}>
                    Description
                  </MDTypography>
                  <MDTypography variant="h6" sx={{ fontFamily: properties.fontFamily, fontSize: "14px", color: "text.primary", py: 0.5 }}>
                    {asset.assetSubDescrip || asset.description || "N/A"}
                  </MDTypography>
                </MDBox>
              </Grid>

              <Grid item xs={12} md={4}>
                <MDBox sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.5 }}>
                  <MDTypography
                    variant="h6"
                    sx={{
                      fontFamily: properties.fontFamily,
                      fontSize: "14px",
                      fontWeight: "bold",
                      color: "textPrimary",
                      mr: 1
                    }}
                  >
                    Status
                  </MDTypography>
                  <Chip
                    label={asset.isUsed ? "Assigned" : "Store"}
                    color={asset.isUsed ? "success" : "warning"}
                    size="small"
                    sx={{ fontFamily: properties.fontFamily }}
                  />

                  {asset.isUsed ? (
                    <MDButton
                      variant="contained"
                      color="info"
                      size="small"
                      sx={{ ml: 2, fontFamily: properties.fontFamily }}
                      onClick={() => navigate("/receiveAsset", { state: { assetId: asset.assetId } })}
                    >
                      Receive Asset
                    </MDButton>
                  ) : (
                    <MDButton
                      variant="contained"
                      color="info"
                      size="small"
                      sx={{ ml: 2, fontFamily: properties.fontFamily }}
                      onClick={() => navigate("/issueAsset", { state: { assetId: asset.assetId, asset } })}
                    >
                      Issue Asset
                    </MDButton>
                  )}
                </MDBox>
              </Grid>

            </Grid>

            {asset.isUsed && (
              <>
                <Divider sx={{ my: 3 }} />
                <MDTypography
                  variant="h6"
                  sx={{ fontFamily: properties.fontFamily, fontWeight: "bold", color: "textPrimary", mb: 2 }}
                >
                  Assignment Information
                </MDTypography>

                <Grid container spacing={3}>
                  <Grid item xs={12} md={4}>
                    <MDBox>
                      <MDTypography
                        variant="h6"
                        sx={{ fontFamily: properties.fontFamily, fontSize: "14px", fontWeight: "bold", color: "textPrimary" }}
                      >
                        Assigned To
                      </MDTypography>
                      <MDTypography
                        variant="h6"
                        sx={{ fontFamily: properties.fontFamily, fontSize: "14px", color: "text.primary", py: 0.5 }}
                      >
                        {asset.empName ? `${asset.empName} [${asset.empId}]` : "N/A"}
                      </MDTypography>
                    </MDBox>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <MDBox>
                      <MDTypography
                        variant="h6"
                        sx={{ fontFamily: properties.fontFamily, fontSize: "14px", fontWeight: "bold", color: "textPrimary" }}
                      >
                        Assigned Date
                      </MDTypography>
                      <MDTypography
                        variant="h6"
                        sx={{ fontFamily: properties.fontFamily, fontSize: "14px", color: "text.primary", py: 0.5 }}
                      >
                        {asset.assignedDate ? new Date(asset.assignedDate).toLocaleDateString("en-GB") : "N/A"}
                      </MDTypography>
                    </MDBox>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <MDBox>
                      <MDTypography
                        variant="h6"
                        sx={{ fontFamily: properties.fontFamily, fontSize: "14px", fontWeight: "bold", color: "textPrimary" }}
                      >
                        Assigned By
                      </MDTypography>
                      <MDTypography
                        variant="h6"
                        sx={{ fontFamily: properties.fontFamily, fontSize: "14px", color: "text.primary", py: 0.5 }}
                      >
                        {asset.assignedBy || "N/A"}
                      </MDTypography>
                    </MDBox>
                  </Grid>
                </Grid>
              </>
            )}
          </CardContent>
        </Card>
      </MDBox>
    </DashboardLayout>
  );
};

export default ViewAsset;
